import * as React from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Box, Flex, HStack, Stack } from "@chakra-ui/react";
import { Skeleton } from "../../components/Skeleton";
import {
  EmptyPlacementCard,
  PlacementCard,
} from "../../components/PlacementCard";
import { SortPanel } from "./SortPanel";
import { RootState } from "../../store";
import { EmptyList } from "../../components/EmptyList";
import { Footer } from "./Footer";
import { useBreakpointValue } from "../../hooks/useBreakpointValue";
import { useConfig } from "../../hooks/useConfig";
import { PlacementId } from "../../types/Placement";
import { LazyList } from "src/components/LazyList";

export interface Props {
  onClick: (
    placementId: PlacementId,
    position: google.maps.LatLngLiteral
  ) => void;
}

export const PlacementList = React.memo(
  ({ onClick }: Props): React.ReactElement | null => {
    const results = useSelector((state: RootState) => state.results);
    const { ids: savedIds } = useSelector((state: RootState) => state.saved);
    const { isBaseBreakpoint } = useBreakpointValue();
    const config = useConfig();

    const [canAddSavedSchool, setCanAddSavedSchool] = React.useState<boolean>(
      config?.maxNumRankable
        ? config.maxNumRankable > 0 && savedIds.length >= config.maxNumRankable
        : true
    );

    React.useEffect(() => {
      if (config?.maxNumRankable)
        setCanAddSavedSchool(
          config.maxNumRankable > 0 && savedIds.length >= config.maxNumRankable
        );
    }, [config, savedIds]);

    return (
      <Flex direction="column" height="100%" width="100%">
        <>
          <SortPanel />
          <Box height="100%" overflowY="auto">
            {results.status === "loading" ? (
              <Stack padding={5} gridGap={5}>
                {Array(5)
                  .fill(null)
                  .map((val, index) => (
                    <HStack gridGap={5} key={index}>
                      <Skeleton
                        startColor="gray.100"
                        endColor="gray.200"
                        height="70px"
                        flexGrow={1}
                      />
                      <Skeleton
                        startColor="gray.100"
                        endColor="gray.200"
                        height="70px"
                        width="70px"
                      />
                    </HStack>
                  ))}
              </Stack>
            ) : results.ids.length === 0 ? (
              <EmptyList
                header={
                  <FormattedMessage
                    id="RESULTS_LIST_EMPTY_HEADER"
                    values={{
                      schools:
                        config?.variableWords.school.plural.label.toLowerCase(),
                    }}
                  />
                }
                description={
                  <FormattedMessage id="RESULTS_LIST_EMPTY_DESCRIPTION" />
                }
              />
            ) : (
              <LazyList
                elements={results.ids}
                placeholder={() => <EmptyPlacementCard />}
              >
                {(chunk) => (
                  <>
                    {chunk.map((id) => (
                      <PlacementCard
                        key={`placement-card-${id}`}
                        canAddSavedSchool={canAddSavedSchool}
                        onClick={onClick}
                        placementId={id || ""}
                      />
                    ))}
                  </>
                )}
              </LazyList>
            )}
          </Box>
          {!isBaseBreakpoint && <Footer />}
        </>
      </Flex>
    );
  }
);
